import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_META = {
  success: { label: 'Success', color: '#00C896' },
  failed:  { label: 'Failed',  color: '#FF4757' },
  blocked: { label: 'Blocked', color: '#ff9900' },
};

const MODULE_COLORS = [
  '#5B8EFF', '#00C896', '#818CF8', '#F5A623', '#FF4757',
  '#ff9900', '#00bcd4', '#e91e63', '#8bc34a', '#9c27b0',
];

function StatusBadge({ value }) {
  const m = STATUS_META[value] || { label: value, color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem',
      borderRadius: 99, textTransform: 'uppercase',
      background: `${m.color}18`, color: m.color,
      border: `1px solid ${m.color}33`,
    }}>{m.label}</span>
  );
}

function MiniBar({ value, max, color }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-2)', width: 28, textAlign: 'right', flexShrink: 0 }}>{value}</span>
    </div>
  );
}

function DailyBar({ series }) {
  if (!series?.length) return null;
  const max = Math.max(...series.map(d => d.count), 1);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 56 }}>
      {series.map((d, i) => {
        const h = Math.max(4, Math.round((d.count / max) * 52));
        const date = new Date(d.date + 'T00:00:00');
        return (
          <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ position: 'relative', width: '100%' }} title={`${d.date}: ${d.count} events`}>
              <div style={{
                width: '100%', height: h, background: i === series.length - 1 ? '#5B8EFF' : 'rgba(91,142,255,0.35)',
                borderRadius: '3px 3px 0 0', transition: 'height .4s',
              }} />
            </div>
            <span style={{ fontSize: '0.6rem', color: 'var(--text-3)' }}>{days[date.getDay()]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const MODULES = ['loans', 'disbursements', 'refunds', 'kyc', 'fraud', 'system', 'api', 'customers', 'audit', 'repayments'];
const PAGE_SIZE = 20;

export default function AuditLogs() {
  const qc = useQueryClient();
  const [module, setModule]   = useState('');
  const [status, setStatus]   = useState('');
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [expanded, setExpanded] = useState(null);

  const { data: statsRaw, isLoading: statsLoading } = useQuery({
    queryKey: ['audit-stats'],
    queryFn: () => adminService.getAuditStats().then(r => r.data.data),
    refetchInterval: 60_000,
    staleTime: 0,
  });

  const { data: logsRaw, isLoading: logsLoading } = useQuery({
    queryKey: ['admin-audit-logs', module, status, search, page],
    queryFn: () => adminService.getAuditLogs({
      ...(module && { module }),
      ...(status && { status }),
      ...(search && { search }),
      page,
      page_size: PAGE_SIZE,
    }).then(r => r.data.data),
    keepPreviousData: true,
    staleTime: 0,
  });

  const stats        = statsRaw || {};
  const logs         = logsRaw?.results ?? logsRaw ?? [];
  const totalCount   = logsRaw?.count ?? 0;
  const totalPages   = Math.ceil(totalCount / PAGE_SIZE) || 1;
  const byModule     = stats.by_module     || [];
  const topActors    = stats.top_actors    || [];
  const dailySeries  = stats.daily_series  || [];
  const recentFails  = stats.recent_failures || [];
  const maxMod       = byModule[0]?.count || 1;

  const resetFilters = () => { setModule(''); setStatus(''); setSearch(''); setPage(1); };

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1>Audit Dashboard</h1>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: 2 }}>
            Immutable activity trail — every admin action is recorded and tamper-proof
          </p>
        </div>
        <button
          className="btn btn-ghost"
          style={{ fontSize: '0.8rem' }}
          onClick={() => { qc.invalidateQueries({ queryKey: ['audit-stats'] }); qc.invalidateQueries({ queryKey: ['admin-audit-logs'] }); }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* ── Top stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Events',   value: stats.total         ?? '…', color: '#5B8EFF', icon: '◉' },
          { label: 'Successful',     value: stats.success_count ?? '…', color: '#00C896', icon: '✓' },
          { label: 'Failed',         value: stats.failed_count  ?? '…', color: '#FF4757', icon: '✗', urgent: stats.failed_count > 0 },
          { label: 'Blocked',        value: stats.blocked_count ?? '…', color: '#ff9900', icon: '⊘', urgent: stats.blocked_count > 0 },
        ].map(c => (
          <div key={c.label} className="stat-card" style={{ position: 'relative', overflow: 'hidden', ...(c.urgent && { borderColor: `${c.color}40` }) }}>
            {c.urgent && (
              <span style={{ position: 'absolute', top: 8, right: 8, width: 8, height: 8, borderRadius: '50%', background: c.color, boxShadow: `0 0 8px ${c.color}`, animation: 'pulse 2s infinite' }} />
            )}
            {statsLoading ? (
              <div style={{ color: 'var(--text-3)', fontSize: '1.5rem' }}>…</div>
            ) : (
              <div className="stat-value" style={{ color: c.color }}>{c.value.toLocaleString?.() ?? c.value}</div>
            )}
            <div className="stat-label">{c.label}</div>
          </div>
        ))}
      </div>

      {/* ── Charts row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>

        {/* Activity last 7 days */}
        <div className="card">
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.75rem' }}>
            Last 7 Days
          </div>
          {statsLoading ? <LoadingSpinner /> : <DailyBar series={dailySeries} />}
        </div>

        {/* Activity by module */}
        <div className="card">
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.75rem' }}>
            Events by Module
          </div>
          {statsLoading ? <LoadingSpinner /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {byModule.slice(0, 6).map((m, i) => (
                <div key={m.module} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    fontSize: '0.68rem', width: 70, textTransform: 'capitalize',
                    color: MODULE_COLORS[i % MODULE_COLORS.length], fontWeight: 600, flexShrink: 0,
                  }}>{m.module}</span>
                  <MiniBar value={m.count} max={maxMod} color={MODULE_COLORS[i % MODULE_COLORS.length]} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top actors */}
        <div className="card">
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.75rem' }}>
            Most Active Users
          </div>
          {statsLoading ? <LoadingSpinner /> : topActors.length === 0 ? (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>No data yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {topActors.map((a, i) => (
                <div key={a['user__email']} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%', background: MODULE_COLORS[i % MODULE_COLORS.length],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>{i + 1}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a['user__email']}
                  </span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-1)' }}>{a.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Recent failures alert ── */}
      {recentFails.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem', padding: 0, borderColor: 'rgba(255,71,87,0.3)' }}>
          <div style={{ padding: '0.875rem 1.1rem', borderBottom: '1px solid rgba(255,71,87,0.2)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF4757', animation: 'pulse 2s infinite', flexShrink: 0 }} />
            <h2 style={{ fontSize: '0.88rem', fontWeight: 700, color: '#FF4757' }}>Recent Failures & Blocked Actions</h2>
          </div>
          <div style={{ padding: '0.5rem 0' }}>
            {recentFails.map(f => (
              <div key={f.id} style={{ padding: '0.6rem 1.1rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <StatusBadge value={f.status} />
                <span style={{ fontSize: '0.78rem', fontFamily: 'monospace', color: 'var(--text-2)' }}>{f.action}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', flex: 1 }}>{f.module}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>{f.user_email || 'System'}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', fontFamily: 'monospace' }}>{formatDate(f.timestamp)}</span>
                {f.error_message && (
                  <span style={{ fontSize: '0.7rem', color: '#FF4757', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.error_message}>
                    {f.error_message}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ width: 220 }}
          placeholder="Search user, action, IP…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="form-input" style={{ width: 160 }} value={module} onChange={e => { setModule(e.target.value); setPage(1); }}>
          <option value="">All Modules</option>
          {MODULES.map(m => (
            <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
          ))}
        </select>
        <select className="form-input" style={{ width: 140 }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="blocked">Blocked</option>
        </select>
        {(module || status || search) && (
          <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }} onClick={resetFilters}>
            Clear filters ✕
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--text-3)' }}>
          {totalCount.toLocaleString()} event{totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Log table ── */}
      <div className="card" style={{ padding: 0 }}>
        {logsLoading ? (
          <div style={{ padding: '2.5rem' }}><LoadingSpinner /></div>
        ) : (
          <div className="table-wrapper">
            <table style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Module</th>
                  <th>Target</th>
                  <th>IP Address</th>
                  <th>Status</th>
                  <th style={{ width: 20 }}></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2.5rem' }}>
                      No audit events match your filters.
                    </td>
                  </tr>
                ) : logs.map(log => (
                  <>
                    <tr
                      key={log.id}
                      style={{
                        cursor: 'pointer',
                        background: log.status !== 'success' ? `${STATUS_META[log.status]?.color || '#ff9900'}06` : '',
                      }}
                      onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                    >
                      <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>
                        {formatDate(log.timestamp)}
                      </td>
                      <td>
                        {log.user_email
                          ? <span>{log.user_email}</span>
                          : <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>System</span>
                        }
                      </td>
                      <td style={{ fontFamily: 'monospace', color: 'var(--text-1)', fontWeight: 500 }}>
                        {log.action}
                      </td>
                      <td>
                        <span style={{ fontSize: '0.72rem', textTransform: 'capitalize', color: 'var(--text-2)' }}>
                          {log.module}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-3)', fontSize: '0.75rem' }}>
                        {log.target_type && (
                          <span>{log.target_type}{log.target_id ? ` · ${log.target_id.slice(0, 8)}` : ''}</span>
                        )}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-3)' }}>
                        {log.ip_address || '—'}
                      </td>
                      <td><StatusBadge value={log.status} /></td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
                        {expanded === log.id ? '▲' : '▼'}
                      </td>
                    </tr>

                    {expanded === log.id && (
                      <tr key={`${log.id}-exp`}>
                        <td colSpan={8} style={{ background: 'var(--surface-alt)', padding: 0, border: 'none' }}>
                          <div style={{ padding: '1rem 1.25rem' }}>

                            {/* Error message */}
                            {log.error_message && (
                              <div style={{
                                padding: '0.6rem 0.875rem', marginBottom: '0.75rem',
                                background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.25)',
                                borderRadius: 8, fontSize: '0.8rem', color: '#FF4757',
                              }}>
                                <strong>Error:</strong> {log.error_message}
                              </div>
                            )}

                            {/* Before / After */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                              <div>
                                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.08em' }}>Before</div>
                                <pre style={{
                                  background: 'var(--surface)', borderRadius: 8, padding: '0.75rem',
                                  overflow: 'auto', fontSize: '0.73rem', color: 'var(--text-2)',
                                  margin: 0, maxHeight: 160, border: '1px solid var(--border)',
                                }}>
                                  {Object.keys(log.old_value || {}).length ? JSON.stringify(log.old_value, null, 2) : '(empty)'}
                                </pre>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.08em' }}>After</div>
                                <pre style={{
                                  background: 'var(--surface)', borderRadius: 8, padding: '0.75rem',
                                  overflow: 'auto', fontSize: '0.73rem', color: 'var(--text-2)',
                                  margin: 0, maxHeight: 160, border: '1px solid var(--border)',
                                }}>
                                  {Object.keys(log.new_value || {}).length ? JSON.stringify(log.new_value, null, 2) : '(empty)'}
                                </pre>
                              </div>
                            </div>

                            {/* Metadata row */}
                            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.75rem', color: 'var(--text-3)' }}>
                              {log.user_agent && (
                                <div><strong style={{ color: 'var(--text-2)' }}>User-Agent: </strong>{log.user_agent}</div>
                              )}
                              {log.target_id && (
                                <div><strong style={{ color: 'var(--text-2)' }}>Target ID: </strong><span style={{ fontFamily: 'monospace' }}>{log.target_id}</span></div>
                              )}
                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <div><strong style={{ color: 'var(--text-2)' }}>Metadata: </strong>{JSON.stringify(log.metadata)}</div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div style={{
            padding: '0.875rem 1.25rem', borderTop: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>
              Page {page} of {totalPages} · {totalCount.toLocaleString()} events
            </span>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button
                className="btn btn-ghost"
                style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                ← Prev
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                return (
                  <button
                    key={p}
                    className={`btn ${p === page ? 'btn-primary' : 'btn-ghost'}`}
                    style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem', minWidth: 32 }}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className="btn btn-ghost"
                style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Immutability note ── */}
      <div style={{
        marginTop: '1.5rem', padding: '0.75rem 1rem',
        background: 'rgba(91,142,255,0.05)', border: '1px solid rgba(91,142,255,0.2)',
        borderRadius: 'var(--radius)', fontSize: '0.78rem', color: 'var(--text-3)',
      }}>
        🔒 Audit records are <strong style={{ color: 'var(--text-2)' }}>immutable</strong> — once written they cannot be modified or deleted, even by administrators.
      </div>
    </div>
  );
}
